// Ported from specter-1 ui/packages/app/src/screens/Research.tsx:109-245.
// Two locked changes from the original:
//   1. Marks carry both data-term (human label, used for hover sync) and
//      data-term-id (URL-safe slug, the stable key into the glossary). This
//      lets renames of the human label stay backward-compatible.
//   2. The `allTerms` argument shape is { term, termId, alternates } so the
//      term-marking pass can pre-compute alternate spellings without
//      re-walking the glossary.

import { useEffect } from "react";
import type { TermKind } from "../data/types";
import { patternsFor, cssEscape } from "./termPatterns";
import { scrollWithinRail } from "./scrollWithinRail";
import { resolveTermFamily, TERM_FAMILY_ATTR } from "./termFamily";

export interface TermEntry {
  term: string;
  termId: string;
  alternates?: string[];
  // Both fields feed data-term-family on the mark. `termFamily` is the
  // explicit reviewer-set value (wins); `kind` is the structural
  // classification used to derive a default when termFamily is absent.
  // Both optional for back-compat with slice-1 fixtures.
  kind?: TermKind;
  termFamily?: string | null;
  // PRD 09 — constituent term_ids when this is a compound. The mark
  // emitter wraps each constituent occurrence inside the outer compound
  // mark; the hover cascade lights inner marks at full intensity and
  // the outer mark with the .is-container-lit soft tier.
  includes?: string[];
}

const SKIP_PARENTS = new Set(["STYLE", "SCRIPT", "MARK"]);

function isInsideMark(node: Node): boolean {
  let cur: Node | null = node.parentNode;
  while (cur && cur instanceof Element) {
    if (SKIP_PARENTS.has(cur.tagName)) return true;
    if (cur.classList.contains("maisight-term-mark")) return true;
    cur = cur.parentNode;
  }
  return false;
}

export function useTermCrossHighlight(
  scrollRef: React.RefObject<HTMLElement | null>,
  mainRef: React.RefObject<HTMLElement | null>,
  allTerms: ReadonlyArray<TermEntry>,
): void {
  useEffect(() => {
    const root = scrollRef.current;
    const main = mainRef.current;
    if (!root || !main) return;

    // Longest terms first so multi-word terms aren't shadowed by their
    // single-word constituents (e.g. "Tier 1" before "Tier"). Alternates
    // are flattened into one entry per spelling, all sharing the same termId.
    const flattened: {
      spelling: string; term: string; termId: string; family: string;
      includes?: string[];
    }[] = [];
    const byTermId = new Map<string, TermEntry>();
    for (const t of allTerms) {
      byTermId.set(t.termId, t);
      const family = resolveTermFamily({
        kind: t.kind, term_family: t.termFamily,
      });
      const includes = t.includes && t.includes.length > 0 ? t.includes : undefined;
      flattened.push({ spelling: t.term, term: t.term, termId: t.termId, family, includes });
      for (const alt of t.alternates ?? []) {
        flattened.push({ spelling: alt, term: t.term, termId: t.termId, family, includes });
      }
    }
    flattened.sort((a, b) => b.spelling.length - a.spelling.length);

    for (const entry of flattened) {
      const basePatterns = patternsFor(entry.spelling);
      const patterns = basePatterns.map(
        (re) => new RegExp(re.source, re.flags.includes("g") ? re.flags : re.flags + "g"),
      );
      const walker = document.createTreeWalker(main, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          if (!node.nodeValue || node.nodeValue.length === 0) return NodeFilter.FILTER_REJECT;
          if (isInsideMark(node)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const targets: Text[] = [];
      let n: Node | null = walker.nextNode();
      while (n) {
        targets.push(n as Text);
        n = walker.nextNode();
      }
      for (const textNode of targets) {
        const text = textNode.nodeValue ?? "";
        // Pick the first pattern that matches — patterns for one term are
        // alternative spellings, not overlapping.
        let matches: RegExpMatchArray[] = [];
        for (const pat of patterns) {
          const ms = [...text.matchAll(pat)];
          if (ms.length > 0) {
            matches = ms;
            break;
          }
        }
        if (matches.length === 0) continue;
        const frag = document.createDocumentFragment();
        let last = 0;
        for (const m of matches) {
          const idx = m.index ?? 0;
          if (idx > last) frag.appendChild(document.createTextNode(text.slice(last, idx)));
          const matchedText = m[0] ?? "";
          const mark = document.createElement("mark");
          mark.className = "maisight-term-mark";
          mark.setAttribute("data-term", entry.term);
          mark.setAttribute("data-term-id", entry.termId);
          mark.setAttribute(TERM_FAMILY_ATTR, entry.family);
          if (entry.includes && entry.includes.length > 0) {
            mark.setAttribute("data-includes", entry.includes.join(" "));
            emitCompoundInner(mark, matchedText, entry.includes, byTermId);
          } else {
            mark.textContent = matchedText;
          }
          frag.appendChild(mark);
          last = idx + matchedText.length;
        }
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        textNode.parentNode?.replaceChild(frag, textNode);
      }
    }

    let activeTerm: string | null = null;
    let activeTermId: string | null = null;
    const setHover = (term: string | null, termId: string | null) => {
      if (term === activeTerm && termId === activeTermId) return;
      if (activeTerm) {
        root.querySelectorAll(`[data-term="${cssEscape(activeTerm)}"]`).forEach((el) =>
          el.classList.remove("is-lit"),
        );
      }
      if (activeTermId) {
        root.querySelectorAll(`[data-includes~="${cssEscape(activeTermId)}"]`).forEach((el) =>
          el.classList.remove("is-container-lit"),
        );
      }
      activeTerm = term;
      activeTermId = termId;
      if (term && termId) {
        root.setAttribute("data-hover-term", term);
        root.setAttribute("data-hover-id", termId);
        root.querySelectorAll(`[data-term="${cssEscape(term)}"]`).forEach((el) =>
          el.classList.add("is-lit"),
        );
        // PRD 09 — soft-light outer compound marks + compound rail rows
        // whose includes list contains the active term_id. Fixes the
        // "constituent hovered, prose stays dark" dead zone for terms
        // that only appear inside compounds.
        root.querySelectorAll(`[data-includes~="${cssEscape(termId)}"]`).forEach((el) =>
          el.classList.add("is-container-lit"),
        );
      } else {
        root.removeAttribute("data-hover-term");
        root.removeAttribute("data-hover-id");
      }
    };
    const onOver = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // data-term-id (not data-term) so the inner constituent mark wins
      // over its outer compound mark when the cursor is on an inner span.
      const hit = target.closest<HTMLElement>("[data-term-id]");
      if (!hit) return;
      const term = hit.getAttribute("data-term");
      const termId = hit.getAttribute("data-term-id");
      if (!term || !termId) return;
      setHover(term, termId);
      // Hover originating from an article instance scrolls the matching
      // glossary row into view *only inside the rail* — see scrollWithinRail
      // for why we don't use Element.scrollIntoView here.
      if (hit.classList.contains("maisight-term-mark")) {
        const row = root.querySelector<HTMLElement>(
          `.maisight-glossary-row[data-term-id="${cssEscape(termId)}"]`,
        );
        const rail = row?.closest<HTMLElement>(".maisight-rail");
        if (row && rail) scrollWithinRail(rail, row);
      }
    };
    const onOut = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const hit = target.closest<HTMLElement>("[data-term-id]");
      if (!hit) return;
      const related = (e as MouseEvent).relatedTarget;
      if (related instanceof Element && related.closest("[data-term-id]") === hit) return;
      setHover(null, null);
    };
    root.addEventListener("mouseover", onOver);
    root.addEventListener("mouseout", onOut);
    root.addEventListener("focusin", onOver);
    root.addEventListener("focusout", onOut);

    return () => {
      root.removeEventListener("mouseover", onOver);
      root.removeEventListener("mouseout", onOut);
      root.removeEventListener("focusin", onOver);
      root.removeEventListener("focusout", onOut);
      setHover(null, null);
    };
  }, [scrollRef, mainRef, allTerms]);
}

// PRD 09 — wrap each constituent occurrence inside a compound mark as
// its own inner <mark>. Plain text runs (whitespace, words not in the
// includes list) stay as bare text nodes. The inner marks carry the
// CONSTITUENT'S term-id + family so the existing hover-by-data-term
// cascade lights them at the same intensity as bare term marks
// elsewhere in the doc.
function emitCompoundInner(
  mark: HTMLElement,
  matchedText: string,
  includes: ReadonlyArray<string>,
  byTermId: ReadonlyMap<string, TermEntry>,
): void {
  interface Range { start: number; end: number; constituent: TermEntry; family: string }
  const ranges: Range[] = [];
  const sortedConstituents = [...includes]
    .map((id) => byTermId.get(id))
    .filter((c): c is TermEntry => !!c)
    .sort((a, b) => b.term.length - a.term.length);

  for (const constituent of sortedConstituents) {
    const spellings = [constituent.term, ...(constituent.alternates ?? [])]
      .sort((a, b) => b.length - a.length);
    const family = resolveTermFamily({
      kind: constituent.kind, term_family: constituent.termFamily,
    });
    for (const spelling of spellings) {
      if (!spelling) continue;
      const re = new RegExp(`\\b${escapeRegex(spelling)}\\b`, "gi");
      let m: RegExpExecArray | null;
      let found = false;
      while ((m = re.exec(matchedText)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        const overlaps = ranges.some((r) => !(end <= r.start || start >= r.end));
        if (overlaps) continue;
        ranges.push({ start, end, constituent, family });
        found = true;
      }
      if (found) break;
    }
  }
  ranges.sort((a, b) => a.start - b.start);

  if (ranges.length === 0) {
    mark.textContent = matchedText;
    return;
  }
  mark.textContent = "";
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) {
      mark.appendChild(document.createTextNode(matchedText.slice(cursor, r.start)));
    }
    const inner = document.createElement("mark");
    inner.className = "maisight-term-mark maisight-term-mark--constituent";
    inner.setAttribute("data-term", r.constituent.term);
    inner.setAttribute("data-term-id", r.constituent.termId);
    inner.setAttribute(TERM_FAMILY_ATTR, r.family);
    inner.textContent = matchedText.slice(r.start, r.end);
    mark.appendChild(inner);
    cursor = r.end;
  }
  if (cursor < matchedText.length) {
    mark.appendChild(document.createTextNode(matchedText.slice(cursor)));
  }
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
